import React, { useState, useEffect } from 'react';
import { collection, doc, onSnapshot, setDoc, deleteDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { PLACEHOLDERS } from '../noticeTemplate';
import { Plus, Trash2, Edit2, Save, X as XIcon } from 'lucide-react';
import { useT } from '../i18n.jsx';

// Maintains the school's notice templates.
//
// Lives in Notice & Payment because that module owns notices, but the templates are not OLE-specific
// -- a general notice drafted by the office uses the same list.
const EMPTY = { name: '', titlePattern: '', body: '', appliesTo: 'both', printTemplateId: '' };

const NoticeTemplates = ({ schoolId, canManage, isMobile }) => {
  const { t } = useT();
  const [templates, setTemplates] = useState([]);
  // The printable counterpart. A school builds the letter in Word -- letterhead, chop, signature
  // line -- and copies the wording into the web version. Pairing them does not stop the two drifting
  // apart, but it makes the drift visible: you can see both in one place, and issuing a notice tells
  // you which document to print. Merging them properly would mean filling placeholders inside the
  // docx, which is a separate piece of work.
  const [printTemplates, setPrintTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!schoolId) return undefined;
    return onSnapshot(
      collection(db, 'schools', schoolId, 'notice_template'),
      (snap) => setTemplates(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => console.error('Error loading notice templates', e),
    );
  }, [schoolId]);

  useEffect(() => {
    if (!schoolId) return undefined;
    return onSnapshot(
      collection(db, 'schools', schoolId, 'ole_templates'),
      (snap) => setPrintTemplates(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => console.error('Error loading print templates', e),
    );
  }, [schoolId]);

  const startNew = () => { setEditing('new'); setForm(EMPTY); };
  const startEdit = (tpl) => { setEditing(tpl.id); setForm({ ...EMPTY, ...tpl }); };

  const handleSave = async () => {
    if (!form.name.trim()) { alert(t('noticeTemplates.needName')); return; }
    setSaving(true);
    try {
      const id = editing === 'new'
        ? doc(collection(db, 'schools', schoolId, 'notice_template')).id
        : editing;
      await setDoc(doc(db, 'schools', schoolId, 'notice_template', id), {
        name: form.name.trim(),
        titlePattern: form.titlePattern.trim(),
        body: form.body,
        appliesTo: form.appliesTo || 'both',
        printTemplateId: form.printTemplateId || '',
        updatedAt: new Date().toISOString(),
        updatedByEmail: (auth.currentUser?.email || '').toLowerCase(),
      }, { merge: true });
      setEditing(null);
    } catch (e) {
      alert(t('noticeTemplates.saveFailed', { message: e.message }));
    }
    setSaving(false);
  };

  const handleDelete = async (tpl) => {
    if (!window.confirm(t('noticeTemplates.deleteConfirm', { name: tpl.name }))) return;
    try {
      await deleteDoc(doc(db, 'schools', schoolId, 'notice_template', tpl.id));
    } catch (e) {
      alert(t('noticeTemplates.deleteFailed', { message: e.message }));
    }
  };

  const insertToken = (token) =>
    setForm((prev) => ({ ...prev, body: `${prev.body || ''}{{${token}}}` }));

  const card = {
    backgroundColor: 'var(--color-bg-card)',
    border: '1px solid var(--color-border)',
    borderRadius: isMobile ? 0 : 'var(--radius-md)',
    padding: isMobile ? '0.8rem' : '1rem',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div>
        <h3 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>{t('noticeTemplates.title')}</h3>
        <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', margin: '0.35rem 0 0', maxWidth: '68ch', lineHeight: 1.7 }}>
          {t('noticeTemplates.intro')}
          <strong>{t('noticeTemplates.introStrong')}</strong>{t('noticeTemplates.introEnd')}
        </p>
      </div>

      {templates.length === 0 && editing === null && (
        <span style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
          {t('noticeTemplates.empty')}
        </span>
      )}

      {templates.map((tpl) => (
        <div key={tpl.id} style={{ ...card, display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
            <strong style={{ fontSize: '0.95rem' }}>{tpl.name}</strong>
            <span style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t(`noticeTemplates.appliesTo.${tpl.appliesTo || 'both'}`, {}, tpl.appliesTo || 'both')}
            </span>
            {tpl.printTemplateId && (
              <span style={{ fontSize: '0.72rem', color: 'var(--color-primary)' }}>
                · {t('noticeTemplates.print', { name: printTemplates.find(pt => pt.id === tpl.printTemplateId)?.name || t('noticeTemplates.printMissing') })}
              </span>
            )}
            {canManage && (
              <span style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
                <button onClick={() => startEdit(tpl)} title={t('common.edit')}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', display: 'flex' }}>
                  <Edit2 size={15} />
                </button>
                <button onClick={() => handleDelete(tpl)} title={t('common.delete')}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', display: 'flex' }}>
                  <Trash2 size={15} />
                </button>
              </span>
            )}
          </div>
          <div style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)', whiteSpace: 'pre-wrap', maxHeight: '6rem', overflow: 'hidden' }}>
            {tpl.body}
          </div>
        </div>
      ))}

      {canManage && editing === null && (
        <button className="btn-outline" onClick={startNew}
                style={{ width: 'auto', padding: '0.55rem 1.1rem', display: 'flex', alignItems: 'center', gap: '0.4rem', alignSelf: 'flex-start' }}>
          <Plus size={15} /> {t('noticeTemplates.newTemplate')}
        </button>
      )}

      {editing !== null && (
        <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
          <div>
            <label className="input-label">{t('noticeTemplates.templateName')}</label>
            <input className="input-field" value={form.name} style={{ margin: 0 }}
                   onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder={t('noticeTemplates.templateNamePlaceholder')} />
          </div>

          <div>
            <label className="input-label">{t('noticeTemplates.usedFor')}</label>
            <select className="input-field" value={form.appliesTo} style={{ margin: 0 }}
                    onChange={(e) => setForm({ ...form, appliesTo: e.target.value })}>
              <option value="both">{t('noticeTemplates.usedForOptions.both')}</option>
              <option value="ole">{t('noticeTemplates.usedForOptions.ole')}</option>
              <option value="general">{t('noticeTemplates.usedForOptions.general')}</option>
            </select>
          </div>

          <div>
            <label className="input-label">{t('noticeTemplates.titleLabel')}</label>
            <input className="input-field" value={form.titlePattern} style={{ margin: 0 }}
                   onChange={(e) => setForm({ ...form, titlePattern: e.target.value })}
                   placeholder={t('noticeTemplates.titlePlaceholder')} />
          </div>

          <div>
            <label className="input-label">{t('noticeTemplates.printable')}</label>
            <select className="input-field" value={form.printTemplateId || ''} style={{ margin: 0 }}
                    onChange={(e) => setForm({ ...form, printTemplateId: e.target.value })}>
              <option value="">{t('noticeTemplates.none')}</option>
              {printTemplates.map((pt) => (
                <option key={pt.id} value={pt.id}>{pt.name || pt.id}</option>
              ))}
            </select>
            <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', margin: '0.3rem 0 0', lineHeight: 1.6 }}>
              {t('noticeTemplates.printableHint')}
            </p>
          </div>

          <div>
            <label className="input-label">{t('noticeTemplates.body')}</label>
            <textarea className="input-field" rows={10} value={form.body}
                      onChange={(e) => setForm({ ...form, body: e.target.value })}
                      style={{ margin: 0, resize: 'vertical', fontFamily: 'inherit' }} />
            <div style={{ marginTop: '0.5rem' }}>
              <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                {t('noticeTemplates.insertPlaceholder')}
              </span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginTop: '0.3rem' }}>
                {PLACEHOLDERS.map((ph) => (
                  <button key={ph.token} type="button" onClick={() => insertToken(ph.token)}
                          title={t(`noticeTemplates.placeholders.${ph.token}`, {}, ph.label)}
                          style={{ fontSize: '0.74rem', padding: '0.15rem 0.45rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', backgroundColor: 'transparent', cursor: 'pointer', fontFamily: 'monospace' }}>
                    {`{{${ph.token}}}`}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
            <button className="btn-outline" onClick={() => setEditing(null)} disabled={saving}
                    style={{ width: 'auto', padding: '0.55rem 1.1rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <XIcon size={15} /> Cancel
            </button>
            <button className="btn-primary" onClick={handleSave} disabled={saving}
                    style={{ width: 'auto', padding: '0.55rem 1.1rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <Save size={15} /> {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NoticeTemplates;
